/**
 * ChartSection Component - Multiple chart types with drill-down
 */
import React from 'react';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import type { ChartType, ChartFilter, SalesDataPoint } from './types';

interface ChartSectionProps {
  data: SalesDataPoint[];
  chartType: ChartType;
  filter: ChartFilter;
  onBarClick?: (data: SalesDataPoint) => void;
  loading?: boolean;
}

const COLORS = {
  bank: '#22c55e',
  crypto: '#3b82f6',
  commission: '#a855f7',
  converted: '#f59e0b',
};

const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: { name: string; value: number; color: string }[]; label?: string }) => {
  if (!active || !payload) return null;
  return (
    <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 shadow-lg">
      <p className="text-white font-medium mb-2">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} style={{ color: entry.color }} className="text-sm">
          {entry.name}: {entry.value.toLocaleString(undefined, { maximumFractionDigits: 2 })}
        </p>
      ))}
    </div>
  );
};

const ChartSkeleton: React.FC = () => (
  <div className="h-80 bg-gray-800 rounded-lg animate-pulse flex items-center justify-center">
    <div className="text-gray-600">Loading chart...</div>
  </div>
);

export const ChartSection: React.FC<ChartSectionProps> = ({ data, chartType, filter, onBarClick, loading }) => {
  if (loading) return <ChartSkeleton />;

  const getVisibleBars = () => {
    if (filter === 'all') return ['bankShares', 'cryptoShares', 'commissionShares', 'convertedShares'];
    if (filter === 'bank') return ['bankShares'];
    if (filter === 'crypto') return ['cryptoShares'];
    if (filter === 'commission') return ['commissionShares'];
    if (filter === 'converted') return ['convertedShares'];
    return [];
  };

  const visibleBars = getVisibleBars();
  const pieData = [
    { name: 'Bank', value: data.reduce((s, d) => s + d.bankShares, 0), color: COLORS.bank },
    { name: 'Crypto', value: data.reduce((s, d) => s + d.cryptoShares, 0), color: COLORS.crypto },
    { name: 'Commission', value: data.reduce((s, d) => s + d.commissionShares, 0), color: COLORS.commission },
    { name: 'Converted', value: data.reduce((s, d) => s + d.convertedShares, 0), color: COLORS.converted },
  ].filter(d => d.value > 0);

  const handleClick = (data: { payload?: SalesDataPoint }) => {
    if (data?.payload && onBarClick) onBarClick(data.payload);
  };

  if (chartType === 'pie') {
    return (
      <ResponsiveContainer width="100%" height={320}>
        <PieChart>
          <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
            {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  if (chartType === 'line') {
    return (
      <ResponsiveContainer width="100%" height={320}>
        <LineChart data={data} onClick={handleClick as any}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis dataKey="label" stroke="#9ca3af" fontSize={12} />
          <YAxis stroke="#9ca3af" fontSize={12} />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          {visibleBars.includes('bankShares') && <Line type="monotone" dataKey="bankShares" name="Bank" stroke={COLORS.bank} strokeWidth={2} dot={{ r: 3 }} />}
          {visibleBars.includes('cryptoShares') && <Line type="monotone" dataKey="cryptoShares" name="Crypto" stroke={COLORS.crypto} strokeWidth={2} dot={{ r: 3 }} />}
          {visibleBars.includes('commissionShares') && <Line type="monotone" dataKey="commissionShares" name="Commission" stroke={COLORS.commission} strokeWidth={2} dot={{ r: 3 }} />}
          {visibleBars.includes('convertedShares') && <Line type="monotone" dataKey="convertedShares" name="Converted" stroke={COLORS.converted} strokeWidth={2} dot={{ r: 3 }} />}
        </LineChart>
      </ResponsiveContainer>
    );
  }

  if (chartType === 'area') {
    return (
      <ResponsiveContainer width="100%" height={320}>
        <AreaChart data={data} onClick={handleClick as any}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis dataKey="label" stroke="#9ca3af" fontSize={12} />
          <YAxis stroke="#9ca3af" fontSize={12} />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          {visibleBars.includes('bankShares') && <Area type="monotone" dataKey="bankShares" name="Bank" fill={COLORS.bank} fillOpacity={0.3} stroke={COLORS.bank} />}
          {visibleBars.includes('cryptoShares') && <Area type="monotone" dataKey="cryptoShares" name="Crypto" fill={COLORS.crypto} fillOpacity={0.3} stroke={COLORS.crypto} />}
          {visibleBars.includes('commissionShares') && <Area type="monotone" dataKey="commissionShares" name="Commission" fill={COLORS.commission} fillOpacity={0.3} stroke={COLORS.commission} />}
          {visibleBars.includes('convertedShares') && <Area type="monotone" dataKey="convertedShares" name="Converted" fill={COLORS.converted} fillOpacity={0.3} stroke={COLORS.converted} />}
        </AreaChart>
      </ResponsiveContainer>
    );
  }

  // Default: Bar chart
  return (
    <ResponsiveContainer width="100%" height={320}>
      <BarChart data={data} onClick={handleClick as any}>
        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
        <XAxis dataKey="label" stroke="#9ca3af" fontSize={12} />
        <YAxis stroke="#9ca3af" fontSize={12} />
        <Tooltip content={<CustomTooltip />} />
        <Legend />
        {visibleBars.includes('bankShares') && <Bar dataKey="bankShares" name="Bank" fill={COLORS.bank} radius={[2, 2, 0, 0]} />}
        {visibleBars.includes('cryptoShares') && <Bar dataKey="cryptoShares" name="Crypto" fill={COLORS.crypto} radius={[2, 2, 0, 0]} />}
        {visibleBars.includes('commissionShares') && <Bar dataKey="commissionShares" name="Commission" fill={COLORS.commission} radius={[2, 2, 0, 0]} />}
        {visibleBars.includes('convertedShares') && <Bar dataKey="convertedShares" name="Converted" fill={COLORS.converted} radius={[2, 2, 0, 0]} />}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default ChartSection;

